import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, CheckCircle2, Users } from "lucide-react";
import { PageHero, Reveal } from "../components/Reveal";
import { CTABanner } from "../components/CTABanner";
import { SERVICES } from "../data/content";

export default function Services() {
  const navigate = useNavigate();
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [hash]);

  return (
    <main data-testid="services-page">
      <PageHero
        eyebrow="Our Services"
        title="DIGITAL SERVICES BUILT AROUND YOUR BUSINESS GOALS."
        sub="Twelve specialised services. One accountable partner. From your first brand asset to a full ERP — everything under one roof."
      />

      <section className="bg-dg-navy pb-24 sm:pb-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          {SERVICES.map((s, i) => (
            <div
              key={s.slug}
              id={s.slug}
              data-testid={`service-section-${s.slug}`}
              className="scroll-mt-28 border-t border-white/8 py-14 sm:py-16 first:border-t-0 first:pt-4"
            >
              <div className={`grid items-start gap-10 lg:grid-cols-2 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                <Reveal>
                  <div className="flex items-center gap-4">
                    <span className="grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-dg-blue to-dg-cyan text-white shadow-lg shadow-dg-blue/30">
                      <s.icon size={24} />
                    </span>
                    <span className="font-mono text-xs text-dg-cyan/60">{String(i + 1).padStart(2, "0")}</span>
                    {s.premium && (
                      <span className="rounded-full border border-dg-cyan/40 bg-dg-cyan/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-dg-cyan">Premium Solution</span>
                    )}
                  </div>
                  <h2 className="font-display mt-6 text-2xl sm:text-3xl font-extrabold text-white">{s.title}</h2>
                  <p className="mt-3 text-base sm:text-lg text-dg-cyan/90">{s.tagline}</p>
                  <div className="mt-6 flex items-start gap-3 rounded-2xl border border-white/8 bg-white/[0.03] p-5">
                    <Users size={17} className="mt-0.5 shrink-0 text-dg-cyan" />
                    <p className="text-sm text-slate-400"><span className="font-semibold text-slate-200">Ideal for:</span> {s.ideal}</p>
                  </div>
                  <button
                    data-testid={`service-cta-${s.slug}`}
                    onClick={() => navigate(s.premium ? "/erp" : `/contact?service=${encodeURIComponent(s.title)}`)}
                    className="group mt-7 inline-flex items-center gap-2 rounded-full bg-dg-cyan px-7 py-3.5 text-sm font-bold text-dg-navy transition-all duration-300 hover:bg-white hover:shadow-[0_0_30px_rgba(0,184,212,0.4)]"
                  >
                    {s.premium ? "Explore ERP Solutions" : `Get ${s.title}`}
                    <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </Reveal>
                <Reveal delay={0.12}>
                  <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                    {s.items.map((it) => (
                      <div key={it} className="flex items-center gap-2.5 rounded-xl border border-white/8 bg-white/[0.03] px-4 py-3 text-[13px] text-slate-300 transition-colors duration-300 hover:border-dg-cyan/30">
                        <CheckCircle2 size={14} className="shrink-0 text-dg-cyan" /> {it}
                      </div>
                    ))}
                  </div>
                </Reveal>
              </div>
            </div>
          ))}
        </div>
      </section>

      <CTABanner title="NOT SURE WHICH SERVICE YOU NEED?" sub="Tell us your goal — we'll map the right mix of services for your budget and stage." />
    </main>
  );
}
